import React, { useState } from 'react';
import { Ban, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';

/**
 * Modal used by the agent to cancel a policy. Prompts for the settlement
 * amount and an optional reason, then triggers onConfirm(payload).
 */
const CancellationDialog = ({ open, policy, onClose, onConfirm }) => {
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [err, setErr] = useState('');

  if (!open || !policy) return null;

  const submit = (e) => {
    e.preventDefault();
    const n = parseFloat(amount);
    if (isNaN(n) || n < 0) {
      setErr('Please enter a valid cancellation amount (e.g. 65.00).');
      return;
    }
    onConfirm({
      amount: Number(n.toFixed(2)),
      reason: reason.trim(),
      cancelledAt: new Date().toISOString(),
      paymentDate: policy.paymentDue,
    });
  };

  return (
    <div
      data-testid="cancellation-dialog"
      className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <form
        onSubmit={submit}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl w-full max-w-md p-6 shadow-2xl border border-[#1F0068]/10"
      >
        <div className="flex items-start justify-between gap-3 mb-4">
          <div>
            <div className="flex items-center gap-2 text-[#FF5C39]">
              <Ban className="w-5 h-5" />
              <div className="text-[10px] uppercase tracking-[0.28em] font-bold">Cancel policy</div>
            </div>
            <h2 className="text-xl font-bold text-[#1F0068] mt-1">Cancel Plan {policy.planNumber}</h2>
            <p className="text-xs text-[#1F0068]/70 mt-1">This will generate a Cancellation Letter and Cancellation Invoice for {policy.firstName} {policy.lastName}.</p>
          </div>
          <button type="button" onClick={onClose} className="text-[#1F0068]/60 hover:text-[#1F0068]"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4">
          <div>
            <Label className="text-[#1F0068] font-semibold">Cancellation amount (£)</Label>
            <Input
              data-testid="cancellation-amount"
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => { setAmount(e.target.value); setErr(''); }}
              placeholder="e.g. 65.00"
              className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10"
              autoFocus
            />
            <p className="text-[10px] text-[#1F0068]/60 mt-1">Enter the settlement amount to bill the customer (admin fee + pro-rata as per T&amp;Cs).</p>
          </div>
          <div>
            <Label className="text-[#1F0068] font-semibold">Reason (optional)</Label>
            <Input
              data-testid="cancellation-reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Customer request, non-payment, etc."
              className="h-11 mt-2 rounded-xl bg-[#FFF6EB] border-[#1F0068]/10"
            />
          </div>
          {err && <div className="text-xs text-[#FF5C39] font-semibold">{err}</div>}
        </div>

        <div className="mt-6 flex items-center justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-full">Keep active</Button>
          <Button
            data-testid="cancellation-confirm"
            type="submit"
            className="rounded-full bg-[#FF5C39] hover:bg-[#e0492b] text-white gap-1.5"
          >
            <Ban className="w-4 h-4" /> Cancel & issue paperwork
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CancellationDialog;
